import { useState, useEffect, useRef } from 'react';
import { ArrowRight, Clock, ShieldCheck, CheckCircle2, TrendingUp, Users, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const useCountUp = (target, duration = 2000, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime;
    const numTarget = parseInt(target.replace(/\D/g, ''));
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * numTarget));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
};

const stats = [
  { value: '80', suffix: '%', label: 'Aides max.', Icon: TrendingUp },
  { value: '15000', suffix: '+', label: 'Foyers aidés', Icon: Users },
  { value: '2', suffix: ' min', label: 'Vérification', Icon: Zap },
];

export const HeroSection = ({ onQualify }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const c0 = useCountUp('80', 1500, visible);
  const c1 = useCountUp('15000', 2000, visible);
  const c2 = useCountUp('2', 800, visible);
  const counts = [c0, c1, c2];

  return (
    <section ref={ref} className="relative overflow-hidden bg-gradient-to-b from-[#f0fdfa] via-white to-white pt-32 pb-16 lg:pt-40 lg:pb-24">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-300/50 to-transparent" />
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-teal-100/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-emerald-100/30 blur-3xl pointer-events-none" />
      <div className="absolute top-20 left-[10%] w-2 h-2 rounded-full bg-teal-400/60 animate-float" />
      <div className="absolute top-40 right-[15%] w-3 h-3 rounded-full bg-emerald-300/40 animate-float delay-300" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <Badge
            data-testid="hero-badge"
            variant="outline"
            className={`mb-6 border-teal-200 text-[#0f766e] bg-teal-50/80 px-4 py-1.5 text-sm font-medium rounded-full animate-fade-in-up`}
          >
            MaPrimeRénov' 2026 — Nouveau barème
          </Badge>

          <h1
            data-testid="hero-title"
            className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1f2937] leading-[1.08] tracking-tight animate-fade-in-up delay-100`}
          >
            Jusqu'à{' '}
            <span className="text-[#0f766e] relative">
              80% d'Aides
              <svg className="absolute -bottom-1 left-0 w-full h-2 text-teal-300/40" viewBox="0 0 200 8" preserveAspectRatio="none">
                <path d="M0 7 Q50 0 100 4 Q150 8 200 1" stroke="currentColor" strokeWidth="3" fill="none" />
              </svg>
            </span>
            <br className="hidden sm:block" />
            {' '}MaPrimeRénov' 2026
          </h1>

          <p className={`mt-6 text-base sm:text-lg text-gray-500 leading-relaxed max-w-2xl mx-auto animate-fade-in-up delay-200`}>
            Vérifiez votre éligibilité pour l'<strong className="text-gray-700">ITE</strong>,{' '}
            <strong className="text-gray-700">Pompe à Chaleur</strong> ou{' '}
            <strong className="text-gray-700">Panneaux Solaires</strong> en 2 minutes.
            Gratuit et sans engagement.
          </p>

          <div className={`mt-8 flex justify-center animate-fade-in-up delay-300`}>
            <Button
              data-testid="hero-cta-btn"
              onClick={onQualify}
              className="h-14 px-8 text-base sm:text-lg font-semibold rounded-xl shadow-lg bg-[#0f766e] hover:bg-[#115e59] text-white transition-all duration-300 hover:scale-[1.02] hover:shadow-xl animate-pulse-subtle"
            >
              Commencer la Qualification
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          <p className="mt-3 text-xs text-gray-400 text-center animate-fade-in-up delay-350">
            Service privé commercial indépendant — Non affilié à l'ANAH ni à maprimerenov.gouv.fr
          </p>

          {/* Trust badges */}
          <div className={`mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3 animate-fade-in-up delay-400`}>
            {[
              { Icon: Clock, text: '2 min chrono' },
              { Icon: ShieldCheck, text: '100% Gratuit' },
              { Icon: CheckCircle2, text: 'Certifié RGE' },
            ].map((badge, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-500">
                <badge.Icon className="h-4 w-4 text-[#0f766e]" />
                <span className="text-sm font-medium">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats strip */}
        <div className={`mt-16 grid grid-cols-3 gap-4 max-w-lg mx-auto animate-fade-in-up delay-500`}>
          {stats.map((stat, i) => (
            <div key={i} className="text-center group">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-teal-50 mb-3 transition-transform duration-300 group-hover:scale-110">
                <stat.Icon className="h-5 w-5 text-[#0f766e]" />
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-[#0f766e]">
                {counts[i].toLocaleString('fr-FR')}{stat.suffix}
              </div>
              <div className="text-xs sm:text-sm text-gray-400 mt-1 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
